import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { MOCK_NOTIFICATIONS } from './mocks';
import type { Notification } from './types';

const MOCK_DELAY_MS = 250;
const QUERY_KEY = ['topbar', 'notifications', 'static-mock'] as const;

export interface UseNotificationsResult {
  items: Notification[];
  unreadCount: number;
  markAllRead: () => void;
  isLoading: boolean;
}

export function useNotifications(): UseNotificationsResult {
  const queryClient = useQueryClient();

  const query = useQuery<Notification[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));
      return MOCK_NOTIFICATIONS;
    },
    staleTime: Number.POSITIVE_INFINITY,
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const current = queryClient.getQueryData<Notification[]>(QUERY_KEY) ?? [];
      return current.map((item) => ({ ...item, read: true }));
    },
    onSuccess: (next) => {
      queryClient.setQueryData<Notification[]>(QUERY_KEY, next);
    },
  });

  const items = query.data ?? [];
  const unreadCount = useMemo(() => items.filter((item) => !item.read).length, [items]);

  return {
    items,
    unreadCount,
    markAllRead: () => markAllReadMutation.mutate(),
    isLoading: query.isLoading,
  };
}
